/* eslint-disable @typescript-eslint/no-explicit-any -- handlers formulaire génériques */
import type { TFunction } from 'i18next';
import { FaPlug } from 'react-icons/fa';
import U2000SaveCancelDialog from '../../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000Alert from '../../../../u2000Core/components/controls/U2000Alert';
import U2000Panel from '../../../../u2000Core/components/layout/U2000Panel';
import { U2000FormStateEnumEditMode } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import { useU3202TestModelHandlers } from '../../../hooks/useU3202TestModelHandlers';
import U3202CatalogModelFields from '../U3202CatalogModelFields';
import { u3202CanTestModelFromGrid } from '../../../utils/u3202ModelTestUtils';
import type { U3202OrgModelConfigGridRow, U3202OrgProviderConfigFormData } from '../../../U3202Service';
import type { U3202OrgModelConfigHandler } from './U3202OrgModelConfigHandlers';

interface U3202CatalogModelDlgProps {
  handler: U3202OrgModelConfigHandler;
  t: TFunction;
  NS: string;
  readonly: boolean;
  parentData: U3202OrgProviderConfigFormData | null;
}

function U3202CatalogModelDlg({ handler, t, NS, readonly, parentData }: U3202CatalogModelDlgProps) {
  const { formStateHandlers } = handler;
  const { testing, testAlert, testSelectedModel, clearTestAlert } = useU3202TestModelHandlers({
    parentData,
  });
  const data = formStateHandlers.getData();
  const isCreate = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const isViewMode = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.NONE;
  const dialogReadOnly = readonly || isViewMode;

  const selectedRow = handler.gridStateHandlers.getSelectedRows()[0] as
      | U3202OrgModelConfigGridRow
      | undefined;
  const canTest =
    !isCreate &&
    !!data?.modelId &&
    selectedRow?.rowState !== 'new' &&
    u3202CanTestModelFromGrid(parentData, 1);

  const onTestModel = () => {
    if (!data?.modelId) {
      return;
    }
    const selectedRow = handler.gridStateHandlers.getSelectedRows()[0] as
      | U3202OrgModelConfigGridRow
      | undefined;
    if (!selectedRow) {
      return;
    }
    void testSelectedModel({
      ...selectedRow,
      modelCode: data.code,
      modelName: data.name,
      parameterSchema: data.parameterSchema,
      parameterValues: data.parameterValues,
    });
  };

  const title = isCreate
    ? t('catalogModel.createTitle', { ns: NS })
    : isViewMode
      ? t('catalogModel.viewTitle', { ns: NS })
      : t('catalogModel.editTitle', { ns: NS });

  return (
    <U2000SaveCancelDialog
      isOpen={handler.isDialogOpen}
      onClose={(action) => {
        clearTestAlert();
        handler.onDlgClose(action);
      }}
      title={title}
      isValid
      readOnly={dialogReadOnly}
      footerStartSlot={
        canTest ? (
          <U2000ActionBtn
            label={t('btnTestModel', { ns: NS })}
            handleClick={onTestModel}
            disabled={testing}
            icon={FaPlug}
            variant="secondary"
          />
        ) : undefined
      }
    >
      <div className="flex min-w-lg max-w-2xl flex-col gap-4">
        {testAlert && (
          <U2000Alert
            isOpen
            type={testAlert.type}
            message={`${testAlert.title} — ${testAlert.message}`}
            onClose={clearTestAlert}
          />
        )}
        <U2000Panel title={t('catalogWizard.sectionModel', { ns: NS })}>
          <U3202CatalogModelFields
            formStateHandlers={formStateHandlers as any}
            data={data as unknown as Record<string, unknown> | null}
            t={t}
            NS={NS}
            readonly={dialogReadOnly}
          />
        </U2000Panel>
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3202CatalogModelDlg;
